import React from 'react';
import { motion } from 'framer-motion';
import { 
  Home, 
  Zap, 
  Calendar, 
  Trophy, 
  Users, 
  Settings,
  Database,
  Tag,
  ChevronRight,
  LogOut,
  Menu,
  X
} from 'lucide-react';

const AdminSidebar = ({ 
  currentPage, 
  onPageChange, 
  isCollapsed, 
  onToggleCollapse,
  onLogout 
}) => {
  const menuItems = [
    {
      id: 'dashboard',
      label: 'แดชบอร์ด',
      icon: Home,
      path: '/admin/dashboard',
      color: 'text-blue-600'
    },
    {
      id: 'pokemon',
      label: 'จัดการโปเกมอน',
      icon: Zap,
      path: '/admin/pokemon',
      color: 'text-green-600',
      submenu: [
        { label: 'รายการโปเกมอน', path: '/admin/pokemon/list' },
        { label: 'เพิ่มโปเกมอนใหม่', path: '/admin/pokemon/add' },
        { label: 'นำเข้าข้อมูล', path: '/admin/pokemon/import' }
      ]
    },
    {
      id: 'data',
      label: 'Data Management Center',
      icon: Database,
      path: '/admin/data/center',
      color: 'text-indigo-600'
    },
    {
      id: 'events',
      label: 'จัดการกิจกรรมและหมวดหมู่',
      icon: Calendar,
      path: '/admin/events',
      color: 'text-purple-600'
    },
    {
      id: 'leagues',
      label: 'จัดการลีก',
      icon: Trophy,
      path: '/admin/leagues',
      color: 'text-yellow-600',
      submenu: [
        { label: 'รายการลีก', path: '/admin/leagues/list' },
        { label: 'เพิ่มลีก', path: '/admin/leagues/add' },
        { label: 'นำเข้าข้อมูลลีก', path: '/admin/leagues/import' }
      ]
    },
    {
      id: 'teams',
      label: 'ทีมแนะนำ',
      icon: Users,
      path: '/admin/teams',
      color: 'text-red-600',
      submenu: [
        { label: 'รายการทีม', path: '/admin/teams/list' },
        { label: 'นำเข้าทีมแนะนำ', path: '/admin/teams/import' }
      ]
    },
    {
      id: 'settings',
      label: 'การตั้งค่า',
      icon: Settings,
      path: '/admin/settings',
      color: 'text-gray-600'
    }
  ];

  const isActive = (path) => {
    if (path === '/admin/dashboard') {
      return currentPage === '/admin' || currentPage === '/admin/dashboard';
    }
    return currentPage.startsWith(path);
  };

  const hasActiveSubmenu = (item) => {
    return item.submenu && item.submenu.some(sub => currentPage.startsWith(sub.path));
  };

  return (
    <motion.div
      initial={false}
      animate={{ width: isCollapsed ? '80px' : '280px' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="bg-white dark:bg-gray-800 shadow-xl border-r border-gray-200 dark:border-gray-700 flex flex-col h-full"
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  Pokedex Admin
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  ระบบจัดการ
                </p>
              </div>
            </motion.div>
          )}
          
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {isCollapsed ? (
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isItemActive = isActive(item.path);
          const hasActiveSub = hasActiveSubmenu(item);
          
          return (
            <div key={item.id}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onPageChange(item.path)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group ${
                  isItemActive || hasActiveSub
                    ? 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border border-blue-200 dark:border-blue-700'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    isItemActive || hasActiveSub
                      ? 'bg-white dark:bg-gray-800 shadow-sm'
                      : 'group-hover:bg-white dark:group-hover:bg-gray-600'
                  }`}>
                    <Icon className={`w-5 h-5 ${
                      isItemActive || hasActiveSub
                        ? item.color
                        : 'text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white'
                    }`} />
                  </div>
                  
                  {!isCollapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className={`font-medium ${
                        isItemActive || hasActiveSub
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'
                      }`}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </div>
                
                {!isCollapsed && item.submenu && (
                  <ChevronRight className={`w-4 h-4 transition-transform ${
                    hasActiveSub ? 'rotate-90' : ''
                  } ${isItemActive || hasActiveSub ? 'text-blue-600' : 'text-gray-400'}`} />
                )}
              </motion.button>

              {/* Submenu */}
              {!isCollapsed && item.submenu && hasActiveSub && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="ml-8 mt-2 space-y-1"
                >
                  {item.submenu.map((subItem) => (
                    <motion.button
                      key={subItem.path}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => onPageChange(subItem.path)}
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${
                        currentPage === subItem.path
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {subItem.label}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          {!isCollapsed && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="font-medium"
            >
              ออกจากระบบ
            </motion.span>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default AdminSidebar;
